<template>
  <div>
    <v-card class="mb-4" outlined>
      <v-toolbar
          color="#488ba6"
          dark
          outlined
      >
        <v-toolbar-title>
          {{ state.member.meeting.meetingInfo.name }}
        </v-toolbar-title>

        <v-spacer />
      </v-toolbar>

      <v-form>
        <v-radio-group
            v-model="state.member.meeting.registrationSelected"
            class="registration-options"
        >
          <v-simple-table class="registration-table">
            <template v-slot:default>
              <thead>
              <tr>
                <th class="text-left">
                  Registration Type
                </th>

                <th class="text-left registration-fee-column">
                  Fee
                </th>

                <th class="text-center registration-choice-column">
                  Choice
                </th>
              </tr>
              </thead>

              <!--
                The backend returns the currently available products
                in registrationProducts.
              -->
              <tbody
                  v-if="
                  state.member.meeting.registrationProducts &&
                  state.member.meeting.registrationProducts.length
                "
              >
              <tr class="registration-section-heading">
                <td colspan="3">
                  <div class="registration-section-title">
                      <span>
                        {{
                          isRegularFeeOpen
                              ? "Regular Registration"
                              : "Early Bird Registration"
                        }}
                      </span>

                    <v-chip
                        small
                        color="success"
                        text-color="white"
                        class="ml-2"
                    >
                      Available Now
                    </v-chip>
                  </div>
                </td>
              </tr>

              <tr
                  v-for="(reg, index) in state.member.meeting.registrationProducts"
                  :key="getProductKey(reg, index, 'current')"
                  class="registration-row registration-row--available"
                  @click="selectRegistration(reg)"
              >
                <td class="registration-description">
                  <strong>{{ reg.name }}</strong>

                  <div
                      v-if="reg.desc"
                      class="registration-details"
                  >
                    {{ reg.desc }}
                  </div>
                </td>

                <td class="registration-price">
                  {{ formatCurrency(reg.price) }}
                </td>

                <td class="text-center">
                  <v-radio
                      :value="reg"
                      :aria-label="`Select ${reg.name}`"
                      @click.stop
                  />
                </td>
              </tr>
              </tbody>

              <!--
                The backend returns the unavailable products
                in notCurrentProducts.
              -->
              <tbody
                  v-if="
                  state.member.meeting.notCurrentProducts &&
                  state.member.meeting.notCurrentProducts.length
                "
                  class="unavailable-registration-products"
              >
              <tr
                  class="
                    registration-section-heading
                    registration-section-heading--unavailable
                  "
              >
                <td colspan="3">
                  <div class="registration-section-title">
                      <span>
                        {{
                          isRegularFeeOpen
                              ? "Early Bird Registration"
                              : "Regular Registration"
                        }}
                      </span>

                    <v-chip
                        small
                        color="grey darken-1"
                        text-color="white"
                        class="ml-2"
                    >
                      {{
                        isRegularFeeOpen
                            ? "No Longer Available"
                            : "Starts January 5, 2027"
                      }}
                    </v-chip>
                  </div>
                </td>
              </tr>

              <tr
                  v-for="(reg, index) in state.member.meeting.notCurrentProducts"
                  :key="getProductKey(reg, index, 'unavailable')"
                  class="registration-row registration-row--unavailable"
              >
                <td class="registration-description">
                  <strong>{{ reg.name }}</strong>

                  <div
                      v-if="reg.desc"
                      class="registration-details"
                  >
                    {{ reg.desc }}
                  </div>

                  <div class="registration-unavailable-message">
                    {{
                      isRegularFeeOpen
                          ? "Early Bird registration has ended."
                          : "Available beginning January 5, 2027."
                    }}
                  </div>

                  <div
                      v-if="reg.unavailableReason"
                      class="registration-unavailable-message"
                  >
                    {{ reg.unavailableReason }}
                  </div>
                </td>

                <td class="registration-price">
                  {{ formatCurrency(reg.price) }}
                </td>

                <td class="text-center">
                  <v-tooltip left>
                    <template v-slot:activator="{ on, attrs }">
                        <span
                            v-bind="attrs"
                            v-on="on"
                            class="disabled-radio-wrapper"
                        >
                          <v-radio
                              :value="reg"
                              disabled
                              :aria-label="
                              `${reg.name} is currently unavailable`
                            "
                          />
                        </span>
                    </template>

                    <span>
                        {{
                        isRegularFeeOpen
                            ? "Early Bird registration is no longer available."
                            : "Regular registration begins January 5, 2027."
                      }}
                      </span>
                  </v-tooltip>
                </td>
              </tr>
              </tbody>

              <!-- No products -->
              <tbody
                  v-if="
                  (
                    !state.member.meeting.registrationProducts ||
                    !state.member.meeting.registrationProducts.length
                  ) &&
                  (
                    !state.member.meeting.notCurrentProducts ||
                    !state.member.meeting.notCurrentProducts.length
                  )
                "
              >
              <tr>
                <td
                    colspan="3"
                    class="text-center py-8 grey--text"
                >
                  No registration types are currently available.
                </td>
              </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-radio-group>
      </v-form>
    </v-card>

    <v-card
        v-if="state.member.meeting.registrationSelected"
        outlined
    >
      <v-toolbar
          color="#488ba6"
          dark
          flat
      >
        <v-toolbar-title>
          Special Event Tickets
        </v-toolbar-title>

        <v-spacer />
      </v-toolbar>

      <ExtraProducts />

      <v-card-actions>
        <v-spacer />

        <v-btn
            color="#488ba6"
            style="color: white;"
            :loading="sent"
            :disabled="sent"
            @click="setupForm"
        >
          Continue
        </v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import ExtraProducts from "@/components/ExtraProducts";

/*

 *
 * January is Eastern Standard Time, so the UTC offset is -05:00.
 */
const REGULAR_REGISTRATION_START = new Date(
    "2027-01-05T00:00:00-05:00"
).getTime();

export default {
  name: "MeetingProducts",

  components: {
    ExtraProducts
  },

  data() {
    return {
      sent: false,
      dialog: false,
      currentTime: Date.now(),
      dateTimer: null
    };
  },

  computed: {
    ...mapGetters({
      state: "getState"
    }),

    /*
     * This is used only for headings and status messages.
     *
     * The backend is responsible for deciding which products
     * are placed in registrationProducts and notCurrentProducts.
     */
    isRegularFeeOpen() {
      return this.currentTime >= REGULAR_REGISTRATION_START;
    }
  },

  mounted() {
    /*
     * Update the label automatically if the page remains open
     * when the registration period changes.
     */
    this.dateTimer = window.setInterval(() => {
      this.currentTime = Date.now();
    }, 60000);
  },

  beforeDestroy() {
    if (this.dateTimer) {
      window.clearInterval(this.dateTimer);
    }
  },

  methods: {
    formatCurrency(price) {
      const numericPrice = Number(price);

      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD"
      }).format(
          Number.isFinite(numericPrice)
              ? numericPrice
              : 0
      );
    },

    getProductKey(registration, index, group) {
      /*
       * Use the database ID when it exists.
       * Fall back to the array index otherwise.
       */
      const id =
          registration.id ||
          registration.ID ||
          registration.product_id ||
          index;

      return `${group}-registration-${id}`;
    },

    selectRegistration(registration) {
      /*
       * Only registrationProducts rows call this method.
       * notCurrentProducts rows remain disabled.
       */
      this.state.member.meeting.registrationSelected =
          registration;
    },

    setupForm() {
      if (!this.state.member.meeting.registrationSelected) {
        return;
      }

      this.sent = true;

      try {
        this.$store.commit("setupForm");
      } finally {
        this.sent = false;
      }
    }
  }
};
</script>

<style scoped>
.registration-options {
  margin-top: 0;
}

.registration-table {
  border: 1px solid #e1e5eb;
  border-radius: 8px;
  overflow: hidden;
}

.registration-table th {
  background-color: #f4f6f8;
  color: #263238 !important;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.registration-fee-column {
  width: 130px;
}

.registration-choice-column {
  width: 90px;
}

.registration-section-heading td {
  padding: 12px 16px !important;
  background-color: #e8f5e9;
  border-bottom: 1px solid #c8e6c9 !important;
}

.registration-section-heading--unavailable td {
  background-color: #f3f4f6;
  border-bottom-color: #dedede !important;
}

.registration-section-title {
  display: flex;
  align-items: center;
  font-size: 0.95rem;
  font-weight: 700;
}

.registration-row {
  transition:
      background-color 0.2s ease,
      box-shadow 0.2s ease;
}

.registration-row--available {
  cursor: pointer;
}

.registration-row--available:hover {
  background-color: #f5f9ff;
  box-shadow: inset 4px 0 0 #488ba6;
}

.registration-row--unavailable {
  background-color: #fafafa;
  color: #888;
  cursor: not-allowed;
}

.registration-row--unavailable strong {
  color: #666;
}

.registration-description {
  min-width: 220px;
  padding-top: 16px !important;
  padding-bottom: 16px !important;
}

.registration-details {
  max-width: 650px;
  margin-top: 4px;
  color: #667085;
  font-size: 0.82rem;
  font-style: italic;
  line-height: 1.45;
}

.registration-row--unavailable .registration-details {
  color: #999;
}

.registration-price {
  white-space: nowrap;
  font-weight: 600;
}

.registration-unavailable-message {
  margin-top: 6px;
  color: #9a6700;
  font-size: 0.78rem;
  font-weight: 500;
}

.disabled-radio-wrapper {
  display: inline-block;
}

@media (max-width: 600px) {
  .registration-table th,
  .registration-table td {
    padding-right: 8px !important;
    padding-left: 8px !important;
  }

  .registration-description {
    min-width: 160px;
  }

  .registration-fee-column {
    width: 90px;
  }

  .registration-choice-column {
    width: 60px;
  }

  .registration-section-title {
    align-items: flex-start;
    flex-direction: column;
    gap: 6px;
  }

  .registration-section-title .v-chip {
    margin-left: 0 !important;
  }
}
</style>